import { Filter, QueryOrigin } from '@fabric-ui/components';
import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import { GraphQLResponse } from 'graphql-request';
import z from 'zod/v4';
import { DwQueryResponse } from '../../hooks/queryObjectAsync/useQueryObjectAsyncWithCachedResults.utils';
import { aql } from '../../utils/aql/aql.utils';
import { field, mergeAnd } from '../../utils/filter';
import { generateUUID } from '../../utils/utils';
import { DwQueryRequest } from '../CustomDashboards/types';
import { queryObjectBuilder } from '../CustomDashboards/types/QueryObjectBuilder';
import { queryObjectRequestBuilder } from '../CustomDashboards/types/QueryObjectRequestBuilder';
import { logsDashboardQueryKeys } from './constants';
import type { LogsSearchParamsState } from './Logs';
import { LOGS_CONST } from './Logs.const';
import { SearchLogsHistogramQuery, SearchLogsPieQuery, SearchLogsQuery, SearchLogsTotalsQuery } from './Logs.graphql';
import { fillHistogramDataGap, getInterval } from './Logs.utils';

export const isExternalProjectionTable = (table: string) =>
  table.toLowerCase() === LOGS_CONST.EXTERNAL_PROJECTION_TABLE || table.toLowerCase() === LOGS_CONST.EXTERNAL_PROJECTION_TABLE_LH;
export const normalizeProjectionTable = (table: string) =>
  table.toLowerCase() === LOGS_CONST.EXTERNAL_PROJECTION_TABLE ? LOGS_CONST.EXTERNAL_PROJECTION_TABLE : table;

const rowSchema = z.object({
  data: z.record(z.string(), z.unknown()),
  id: z.string(),
});

const totalsSchema = z.object({
  data: z.record(z.string(), z.number()),
  id: z.string(),
});

export const responseSchema = z.object({
  rows: rowSchema.array(),
  totals: totalsSchema.optional().nullable(),
  totalRowCount: z.number().optional().nullable(),
  queryId: z.string().optional(),
});

export type LogsQueryObjectResponse = z.infer<typeof responseSchema>;

const totalsQueryObjectResponseSchema = responseSchema.extend({
  rows: z.tuple([rowSchema.extend({ data: z.object({ total: z.coerce.number() }) })]),
});

export type Api = (
  query: string,
  opts?: { options?: Record<PropertyKey, unknown>; signal?: AbortSignal }
) => Promise<GraphQLResponse<{ queryObject: DwQueryResponse; viewProjections: any; querySql: DwQueryResponse }>>;

export type CreateQueryParams = {
  queryObject: DwQueryRequest;
  api: Api;
  lastSearch?: Date;
  page: number;
  pieFilter?: Filter | null;
  enabled?: boolean;
};

export const createBasicLogsQuery = ({ api, queryObject, lastSearch, page, pieFilter, enabled }: CreateQueryParams) => {
  const offset = page * LOGS_CONST.TABLE_PAGE_SIZE;
  const queryBuilder = queryObjectBuilder.from(queryObject).setGroupOthers(false).setLimit(LOGS_CONST.TABLE_PAGE_SIZE).setOffset(offset);

  if (pieFilter) {
    const mergedFilter = queryObject.filter ? mergeAnd(queryObject.filter, pieFilter) : pieFilter;
    queryBuilder.setFilter(mergedFilter);
  }

  const query = queryBuilder.build();
  const req = queryObjectRequestBuilder
    .create()
    .setQuery(query)
    .setOrigin(QueryOrigin.INTERACTIVE_QUERY)
    .setTotalRowCount(true)
    .setQueryId(createQueryId('SearchLogsQuery'))
    .build();
  return queryOptions({
    enabled: Boolean(lastSearch && (enabled ?? true)),
    queryKey: logsDashboardQueryKeys.search(query, lastSearch),
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }) => {
      const res = await api(SearchLogsQuery, { options: { req }, signal });
      if (res.errors) {
        throw res.errors;
      }
      return responseSchema.parse(res.data?.queryObject);
    },
  });
};

export const createTotalsQuery = ({ api, lastSearch, queryObject: originalQueryObject, pieFilter, enabled }: CreateQueryParams) => {
  const timestampDimension = originalQueryObject.select.dims.find(
    d => d.name.includes('time') || d.name.includes(LOGS_CONST.VIEW_PROJECTION_TIMESTAMP_NAME)
  );
  const queryBuilder = queryObjectBuilder
    .from(originalQueryObject)
    .setDimensions(queryObjectBuilder.select.dim(`count(${timestampDimension?.name})`, 'total'))
    .setLimit(1)
    .setOffset(0)
    .setGroupOthers(false)
    .setSorting();

  if (pieFilter) {
    const mergedFilter = originalQueryObject.filter ? mergeAnd(originalQueryObject.filter, pieFilter) : pieFilter;
    queryBuilder.setFilter(mergedFilter);
  }
  const queryObject = queryBuilder.build();
  const req = queryObjectRequestBuilder
    .create()
    .setQuery(queryObject)
    .setOrigin(QueryOrigin.INTERACTIVE_QUERY)
    .setQueryId(createQueryId('SearchLogsTotalsQuery'))
    .build();

  return queryOptions({
    enabled: Boolean(lastSearch && timestampDimension?.name && (enabled ?? true)),
    queryKey: logsDashboardQueryKeys.totals(queryObject, lastSearch),
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }) => {
      const res = await api(SearchLogsTotalsQuery, { options: { req }, signal });
      if (res.errors) {
        throw res.errors;
      }
      return z.number().parse(totalsQueryObjectResponseSchema.parse(res.data?.queryObject).rows.at(0)?.data.total);
    },
  });
};

export const createPieQuery = ({
  queryObject: originalQueryObject,
  field: breakdownField,
  top,
  lastSearch,
  api,
}: Pick<CreateQueryParams, 'api' | 'lastSearch' | 'queryObject'> & {
  field?: string | null;
  top: number;
}) => {
  const fieldAlias = 'name' as const;
  const queryObject = queryObjectBuilder
    .from(originalQueryObject)
    .setDimensions(queryObjectBuilder.select.dim(breakdownField || '', fieldAlias))
    .setMetrics(queryObjectBuilder.select.metric(aql.count('*'), 'count'))
    .setGroupBy('count')
    .setLimit(top)
    .setGroupOthers(true)
    .setOffset(0)
    .setSorting(queryObjectBuilder.sorting.desc('count'))

    .build();

  const req = queryObjectRequestBuilder
    .create()
    .setQuery(queryObject)
    .setOrigin(QueryOrigin.INTERACTIVE_QUERY)
    .setQueryId(createQueryId('SearchLogsPieQuery'))
    .build();

  return queryOptions({
    enabled: !!lastSearch && !!breakdownField,
    queryKey: logsDashboardQueryKeys.pie(queryObject, lastSearch),
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }) => {
      const res = await api(SearchLogsPieQuery, { options: { req }, signal });
      if (res.errors) throw res.errors;
      return responseSchema
        .transform(res => ({
          ...res,
          rows: res.rows.map(r => {
            const name =
              typeof r.data.name === 'object' && r.data.name && !Array.isArray(r.data.name)
                ? JSON.stringify(r.data.name)
                : String(r.data.name);
            return {
              id: r.id || name,
              count: z.coerce.number().parse(r.data.count),
              [fieldAlias]: name,
            };
          }),
        }))
        .parse(res.data?.queryObject).rows;
    },
  });
};

export const createHistogramQuery = ({
  api,
  queryObject: { top, filter, ...originalQueryObject },
  lastSearch,
  date: [from, to],
  table,
  pieFilter,
}: Pick<CreateQueryParams, 'api' | 'lastSearch' | 'queryObject' | 'pieFilter'> & {
  date: LogsSearchParamsState['date'];
  table: string;
}) => {
  const { amount, unit, ms } = getInterval(100, [from, to]);

  const alias = 'date';
  const timestampField = isExternalProjectionTable(table) ? 'time' : `${table}.event_timestamp`;
  const dimension = aql.histogramBucket(timestampField, from.toISOString(), to.toISOString(), amount, unit);

  const notEmptyFilter = field(alias).date.notEmpty();

  const mergedWithNotEmptyFilter = filter ? mergeAnd(filter, notEmptyFilter) : notEmptyFilter;

  const queryBuilder = queryObjectBuilder
    .from(originalQueryObject)
    .setDimensions(queryObjectBuilder.select.dim(dimension, alias))
    .setMetrics(queryObjectBuilder.select.metric(aql.count(timestampField), 'count'))
    .setGroupBy(alias)
    .setSorting(queryObjectBuilder.sorting.asc(alias))
    .setFilter(mergedWithNotEmptyFilter)
    .setLimit(40000);

  if (pieFilter) {
    const mergedFilter = mergedWithNotEmptyFilter ? mergeAnd(mergedWithNotEmptyFilter, pieFilter) : pieFilter;
    queryBuilder.setFilter(mergedFilter);
  }

  const query = queryBuilder.build();
  const req = queryObjectRequestBuilder
    .create()
    .setQuery(query)
    .setOrigin(QueryOrigin.INTERACTIVE_QUERY)
    .setQueryId(createQueryId('SearchLogsHistogramQuery'))
    .build();

  return queryOptions({
    enabled: !!lastSearch,
    queryKey: logsDashboardQueryKeys.histogram(query, lastSearch),
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }) => {
      const res = await api(SearchLogsHistogramQuery, { options: { req }, signal });
      if (res.errors) {
        throw res.errors;
      }
      const parsed = responseSchema
        .transform(value => ({
          ...value,
          rows: value.rows
            .filter(row => row.data.date !== null) // Filter out null dates (temp fix until BE fixes)
            .map(row => ({
              id: row.id,
              date: z.coerce.date().parse(row.data.date).toISOString(),
              count: z.coerce.number().parse(row.data.count),
            })),
        }))
        .parse(res.data?.queryObject).rows;

      return fillHistogramDataGap([from, to], ms, parsed).map(row => ({
        ...row,
        date: row.date.toISOString(),
      }));
    },
  });
};

const createQueryId = (prefix: string) => `${prefix}_${generateUUID()}`;
